import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { trpc } from 'pos-client/utils/trpc.ts';
import type { LocationModel } from 'pos-shared/models/locationSchema.ts';

import { useConfirmModal } from './useConfirmModal.tsx';
import { useNotifications } from './useNotifications.tsx';

export const useLocationModal = ({
  location,
}: {
  location: LocationModel | undefined;
}) => {
  const { t } = useTranslation();
  const trpcContext = trpc.useUtils();
  const { showSuccess, showError } = useNotifications();
  const navigate = useNavigate();

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const showModal = () => setIsModalOpen(true);
  const hideModal = () => setIsModalOpen(false);

  const { mutate: deleteLocation } =
    trpc.locations.removeServiceLocation.useMutation({
      onSuccess: async () => {
        await trpcContext.locations.findLocations.refetch();
        showSuccess(
          t('common.notification.success.message', {
            action: 'deleted',
            entity: 'service location',
          })
        );

        navigate('/locations');
      },
      onError: async () => {
        showError(
          t('common.notification.error.message', {
            action: 'delete',
            entity: 'service location',
          })
        );
      },
    });

  const { mutate: updateServiceLocationStatus } =
    trpc.locations.updateServiceLocationStatus.useMutation({
      onSuccess: async () => {
        await trpcContext.locations.findLocations.refetch();
        showSuccess(
          t('common.notification.success.message', {
            action: 'update',
            entity: 'service location status',
          })
        );
      },
      onError: async () => {
        showError(
          t('common.notification.error.message', {
            action: 'update',
            entity: 'service location status',
          })
        );
      },
    });

  const { showModal: confirmDeleteLocation } = useConfirmModal({
    type: 'delete',
    content: t('locations.deleteServiceLocationConfirm'),
    confirmText: t('locations.deleteServiceLocation'),
    confirmProps: {
      ghost: true,
    },
  });

  const deleteLocationHandler = () => {
    if (location) {
      confirmDeleteLocation({
        title: `${t('locations.deleteServiceLocation')} ${location.name}`,
        onConfirm() {
          deleteLocation({ id: location.id });
        },
      });
    }
  };

  const updateLocationStatusHandler = (isActive?: boolean) => {
    const locationActive = !isActive;
    if (location) {
      updateServiceLocationStatus({
        id: location.id,
        isActive: locationActive,
      });
    }
  };

  return {
    isLocationModalOpen: isModalOpen,
    showLocationModal: showModal,
    hideLocationModal: hideModal,
    updateLocationStatusHandler,
    deleteLocationHandler,
  };
};
